import React, { forwardRef, useRef, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { customScrollBarStyles } from "../../../assets/MUIStyles/customScrollBar";

interface CustomScrollbarsProps {
  height?: string;
  className?: string;
  children: React.ReactNode;
  hideTimeout?: number;
  thumbColor?: string;
  thumbHoverColor?: string;
  scrollbarWidthPx?: number;
}

const CustomScrollbars = forwardRef<HTMLDivElement, CustomScrollbarsProps>(
  (
    {
      height = "85vh",
      className = "",
      children,
      hideTimeout = 1500,
      thumbColor = "#6A6A6A",
      thumbHoverColor = "rgba(0, 0, 0, 0.5)",
      scrollbarWidthPx = 3,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const thumbRef = useRef<HTMLDivElement>(null);
    const thumbHeightRef = useRef(0);
    const thumbTopRef = useRef(0);
    const [visible, setVisible] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const dragging = useRef(false);
    const startY = useRef(0);
    const startScrollTop = useRef(0);
    const rafId = useRef<number | null>(null);

    useEffect(() => {
      if (!ref) return;
      if (typeof ref === "function") ref(containerRef.current);
      else
        (ref as React.MutableRefObject<HTMLDivElement | null>).current =
          containerRef.current;
    }, [ref]);

    useEffect(() => {
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (rafId.current) cancelAnimationFrame(rafId.current);
      };
    }, []);

    const updateThumbPositionRaf = () => {
      rafId.current = requestAnimationFrame(() => {
        if (!containerRef.current || !contentRef.current || !thumbRef.current)
          return;

        const container = contentRef.current;
        const containerHeight = containerRef.current.clientHeight;
        const contentHeight = container.scrollHeight;

        if (contentHeight <= containerHeight) {
          setVisible(false);
          return;
        }

        const scrollRatio = containerHeight / contentHeight;
        const thumbHeightValue = Math.max(scrollRatio * containerHeight, 30);

        const scrollTop = container.scrollTop;
        const maxScrollTop = contentHeight - containerHeight;
        const thumbTopValue =
          (scrollTop / maxScrollTop) * (containerHeight - thumbHeightValue);

        thumbHeightRef.current = thumbHeightValue;
        thumbTopRef.current = thumbTopValue;

        if (thumbRef.current) {
          thumbRef.current.style.height = `${thumbHeightValue}px`;
          thumbRef.current.style.top = `${thumbTopValue}px`;
        }

        showScrollbar();
      });
    };

    const showScrollbar = () => {
      if (contentRef.current && containerRef.current) {
        const contentHeight = contentRef.current.scrollHeight;
        const containerHeight = containerRef.current.clientHeight;

        if (contentHeight <= containerHeight) {
          setVisible(false);
          return;
        }
      }

      setVisible(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setVisible(false), hideTimeout);
    };

    useEffect(() => {
      rafId.current = requestAnimationFrame(updateThumbPositionRaf);

      let resizeTimer: NodeJS.Timeout | null = null;
      const handleResize = () => {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          updateThumbPositionRaf();
        }, 100);
      };

      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
        if (resizeTimer) clearTimeout(resizeTimer);
      };
    }, []);

    const handleThumbMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragging.current = true;
      startY.current = e.clientY;
      startScrollTop.current = contentRef.current?.scrollTop || 0;
      document.addEventListener("mousemove", handleThumbMouseMove);
      document.addEventListener("mouseup", handleThumbMouseUp);
      document.body.style.userSelect = "none";
    };

    const handleThumbMouseMove = (e: MouseEvent) => {
      if (!dragging.current || !containerRef.current || !contentRef.current)
        return;

      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        const deltaY = e.clientY - startY.current;
        const container = containerRef.current!;
        const content = contentRef.current!;

        const scrollRatio = content.scrollHeight / container.clientHeight;
        const scrollDelta = deltaY * scrollRatio;

        content.scrollTop = startScrollTop.current + scrollDelta;
      });
    };

    const handleThumbMouseUp = () => {
      dragging.current = false;
      document.removeEventListener("mousemove", handleThumbMouseMove);
      document.removeEventListener("mouseup", handleThumbMouseUp);
      document.body.style.userSelect = "";
    };

    const handleScroll = () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      updateThumbPositionRaf();
    };

    return (
      <Box
        ref={containerRef}
        sx={{ ...customScrollBarStyles.mainContainer, height }}
        className={className}
        onMouseEnter={showScrollbar}
        onMouseLeave={() => setVisible(false)}
      >
        <Box
          ref={contentRef}
          className="custom-scroll-content"
          sx={customScrollBarStyles.contentContainer}
          onScroll={handleScroll}
        >
          {children}
        </Box>

        <Box
          sx={{
            ...customScrollBarStyles.thumbContainer,
            width: `${scrollbarWidthPx}px`,
            opacity: visible ? 1 : 0,
          }}
          onClick={(e) => {
            if (!containerRef.current || !contentRef.current) return;

            if (rafId.current) cancelAnimationFrame(rafId.current);
            rafId.current = requestAnimationFrame(() => {
              const clickY = e.nativeEvent.offsetY;
              const container = containerRef.current!;
              const content = contentRef.current!;
              const thumbHalf = thumbHeightRef.current / 2;

              const ratio =
                (clickY - thumbHalf) /
                (container.clientHeight - thumbHeightRef.current);
              const scrollTo =
                ratio * (content.scrollHeight - container.clientHeight);

              content.scrollTop = scrollTo;
            });
          }}
        >
          <Box
            ref={thumbRef}
            sx={{
              width: `${scrollbarWidthPx}px`,
              backgroundColor: thumbColor,
              borderRadius: `${scrollbarWidthPx / 2}px`,
              "&:hover": {
                backgroundColor: thumbHoverColor,
              },
              ...customScrollBarStyles.thumb,
            }}
            onMouseDown={handleThumbMouseDown}
          />
        </Box>
      </Box>
    );
  }
);

export default CustomScrollbars;
